import { addModule, getRegistry } from './registry';
import { createStep, addToStream } from './stream';
import { parseModuleId, parseModuleDeps } from './utils/parser';
import * as experiments from './utils/experiments';
import * as versions from './utils/versions';
import { wrapBench } from '@tcc/stopwatch';

/**
 * Define function that replicates the AMD behavior but wraps all its behavior
 * in a promise. Use it to require modules from the private registry or
 * to get external resources from our private packages
 * @param {String?} moduleId
 * @param {Array?} dependencies
 * @param {Function|Object} fx Module Factory
 * @return {String} url
 */
export default function _define(moduleId, dependencies, fx) {
  // Build a new step in the execution stream
  const newStep = createStep({
    id: parseModuleId(moduleId),

    deps: parseModuleDeps(moduleId, dependencies),

    // Get the factory as the last present argument
    factory: fx || dependencies || moduleId,

    /**
     * Function that will be executed for this step
     * @param {Object} step reference to self
     * @return {Promise}
     */
    callback: function stepCallback({ id, deps, factory }) {
      if (__DEV__) {
        // This is useful when testing in dev, so i'm going to leave the console.log
        // eslint-disable-next-line no-console,no-inline-comments
        console.log('Callback executed for :', id); // NOSONAR
      }

      // Wrap the function execution for benchmarking
      const wrappedAddModule = wrapBench(
        'loadlifter_addModule_' + id,
        addModule
      );

      return wrappedAddModule(id, factory, deps);
    }
  });

  if (__DEV__) {
    // This is useful when testing in dev, so i'm going to leave the console.log
    // eslint-disable-next-line no-console,no-inline-comments
    console.log('Add module with define:', newStep.id); // NOSONAR
  }

  // Wrap the function execution for benchmarking
  const wrappedAddToStream = wrapBench(
    'loadlifter_addToStream_' + newStep.id,
    addToStream
  );

  return wrappedAddToStream(newStep).then(val =>
    // Store versions in the localStorage after it has been resolved
    // @TODO we need to find a better place for this to happen.
    versions.store() && val
  );
}

// This tells the umd packages to use the global define function
_define.amd = {};

// Verirfy that we have a loadLifter instance already in global scope
_define.lift = true;

// Interface for Experiments Management
_define.experiments = experiments;

// Interface for Versions Management (Read Only)
_define.versions = versions;

_define.registry = { getRegistry };
